import '../src/utilities/i18n';
import 'react-toastify/dist/ReactToastify.css';

import MsgGetToken from '../src/utilities/MsgGetToken';
import ScrollToTop from "./utilities/ScrollToTop";

import { Suspense, useEffect, useState, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useBootstrapDirection } from "./hooks/useBootstrapDirection";

import { HomeRoutes } from './routes/HomeRoutes';
import { HelmetProvider } from 'react-helmet-async';
import { UserProvider } from "./context/User/UserContext";
import { LibraryProvider } from "./context/LibraryContext";
import { LibCategoryProvider } from './context/LibraryCategoryContext';
import { AdminRoutes } from "./routes/AdminRoutes";
import { UserRoutes } from './routes/UserRoutes';

const ActiveUserPassword = lazy(() => import("./features/user/login/registerActiveUser"));

// import ActiveUserPassword from './features/user/login/registerActiveUser';

// Language wrapper component that handles language switching
function LanguageWrapper() {
  const { lang } = useParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (['ar', 'en'].includes(lang)) {
      i18n.changeLanguage(lang);
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    }
  }, [lang, i18n]);

  // Return the main content with all your routes
  return <MainContent />;
}

// Main content component that contains your routes
function MainContent() {
  const { i18n } = useTranslation();
  useBootstrapDirection(i18n.language, { i18n });
  const [, setSchema] = useState(null);
  const [, setLoading] = useState(true);

  useEffect(() => {
    fetch('/schema.json')
      .then(response => response.json())
      .then(data => {
        setSchema(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading schema:', error);
        setLoading(false);
      });
  }, []);

  return (
    <Suspense fallback={<div className="loader" role="status" aria-live="polite" aria-label="Loading"></div>}>
      <Routes>
        {/* Your existing routes but without the language prefix since it's already handled */}
        {HomeRoutes()}
        {UserRoutes()}
      </Routes>
    </Suspense>
  );
}

function AdminLanguageWrapper() {
  const { i18n } = useTranslation();
  useBootstrapDirection(i18n.language, { i18n });

  return (
    <Suspense fallback={<div className="loader" role="status" aria-live="polite" aria-label="Loading"></div>}>
      <Routes>
        {AdminRoutes()}
      </Routes>
    </Suspense>
  );
}

function App() {

  function getDefaultLanguage() {
    // First check if user has previously selected a language
    const savedLang = localStorage.getItem('i18nextLng');
    return savedLang;
  }

  return (
    <HelmetProvider>
      <UserProvider>
        <LibraryProvider>
          <LibCategoryProvider>

            <BrowserRouter>
              <MsgGetToken />
              <ToastContainer
                position="top-right"
                pauseOnFocusLoss
                pauseOnHover
                theme="colored"
                autoClose={3000}
              />
              <ScrollToTop />

              <Suspense fallback={<div className="loader" role="status" aria-live="polite" aria-label="Loading"></div>}>
                <Routes>
                  {/* Redirect services routes without language prefix */}
                  <Route
                    path="/services/*"
                    element={<Navigate to={`/${getDefaultLanguage()}/services`} replace />}
                  />
                  {/* Redirect root to default language */}
                  <Route path="/" element={<Navigate to={`/${getDefaultLanguage()}/`} replace />} />
                  {/* All language-prefixed routes go through LanguageWrapper */}
                  <Route path="/:lang/*" element={<LanguageWrapper />} />
                  <Route path="/admin/*" element={<AdminLanguageWrapper />} />
                  <Route path="/auth/reset-password/:token" element={<ActiveUserPassword />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </LibCategoryProvider>
        </LibraryProvider>
      </UserProvider>
    </HelmetProvider>
  );
}

export default App;